/**
 * iter307c — Shared sort util for the admin Invoice tables.
 *
 * Replaces the in-component `sorter` in InvoiceManagement.js +
 * ParentInvoicesPanel.jsx (both used identical-but-subtly-bugged
 * inline sorters). The two real bugs being fixed:
 *
 *  1. Date columns (`paid_at`, `created_at`, etc.) were being string-
 *     compared via `a[key] < b[key]`. That MOSTLY works for ISO
 *     strings with the same offset, but null rows coerced to `''` got
 *     `'' < anyDateString === true`, so null-paid (pending) rows
 *     floated to the top in asc — confusing alongside paid rows. And
 *     a `paid_date: '2026-06-02'` row would sort AFTER
 *     `paid_at: '2026-06-02T10:00Z'` even though they're the same day.
 *
 *  2. `default: aVal = a[sortColumn] || ''` blew away `0` and `false`
 *     too, breaking numeric / boolean future columns.
 *
 * Returns a NEW array; never mutates input.
 */
export const INVOICE_DATE_FIELDS = new Set([
  'created_at', 'paid_at', 'paid_date', 'due_date', 'updated_at',
  'refunded_at', 'voided_at',
]);

const INVOICE_NUMERIC_FIELDS = new Set([
  'amount', 'original_amount', 'discount_amount', 'monthly_fee',
  'amount_charged', 'refund_amount',
]);

// iter334by — For `payment_method` and `status`, sort by the SAME
// string the UI badge renders (via resolveDisplayMethod +
// effectiveStatus). Previously the sort read raw fields
// (payment_method_type / auto_pay_method / payment_method) which
// caused rows displaying as identical "ACH" to sort separately (one
// stamped `us_bank_account`, another stamped `ach`, another via
// `auto_charge`+`auto_pay_method`), and fully-refunded rows sorted
// as "paid" even though the badge showed "REFUNDED".
const _paymentMethodDisplayKey = (r) => {
  const top = String(r.payment_method || '').toLowerCase();
  const type = String(r.payment_method_type || '').toLowerCase();
  const autoPay = String(r.auto_pay_method || '').toLowerCase();
  // Stripe-paid → rail in payment_method_type
  if (top === 'stripe' && type) {
    if (type === 'us_bank_account' || type === 'ach' || type === 'bank' || type === 'bank_transfer') return 'ach';
    if (type === 'card') return 'card';
    if (type === 'link') return 'link';
    return type;
  }
  // Auto-charged via saved PM → rail in auto_pay_method, prefixed to
  // cluster all Auto-Pay rows together in the sort order.
  if (top === 'auto_charge' && autoPay) {
    let rail = autoPay;
    if (autoPay === 'us_bank_account' || autoPay === 'ach' || autoPay === 'bank' || autoPay === 'bank_transfer') rail = 'ach';
    else if (autoPay === 'card') rail = 'card';
    return `auto-pay · ${rail}`;
  }
  // Fallback: alias ACH-family to the same key
  if (top === 'us_bank_account' || top === 'ach' || top === 'bank' || top === 'bank_transfer') return 'ach';
  if (top === 'facility' || top === 'online') return ''; // hidden in UI
  return top;
};

const _effectiveStatusKey = (r) => {
  const refunded = Number(r.refunded_amount || 0);
  const charged = Number(r.amount_charged || r.amount || 0);
  const status = String(r.status || '').toLowerCase();
  const isEffectivelyRefunded = refunded > 0 && charged > 0 && refunded + 0.005 >= charged;
  if (isEffectivelyRefunded && status !== 'void' && status !== 'refunded') return 'refunded';
  return status;
};

// Map clickable UI header key → row field(s) to read.
// For each, return the FIRST non-empty value.
const INVOICE_FIELD_ACCESSOR = {
  parent: (r) => r.parent_name || r.parent_email || '',
  player: (r) => r.player_names || '',
  payment_method: _paymentMethodDisplayKey,
  status: _effectiveStatusKey,
  auto_pay: (r) => r.auto_pay_method || (r.payment_method === 'auto_charge' ? 'zzz' : ''),
};

const _readField = (row, key) => {
  const accessor = INVOICE_FIELD_ACCESSOR[key];
  if (accessor) return accessor(row);
  return row[key];
};

export function sortInvoices(rows, sortKey, sortDirection = 'desc') {
  if (!Array.isArray(rows) || rows.length === 0) return rows || [];
  if (!sortKey) return rows;
  const dir = sortDirection === 'asc' ? 1 : -1;

  const out = [...rows];
  out.sort((a, b) => {
    const rawA = _readField(a, sortKey);
    const rawB = _readField(b, sortKey);

    // Date branch — null/empty always sinks regardless of direction.
    if (INVOICE_DATE_FIELDS.has(sortKey)) {
      const aHas = rawA != null && rawA !== '';
      const bHas = rawB != null && rawB !== '';
      if (!aHas && !bHas) return 0;
      if (!aHas) return 1;
      if (!bHas) return -1;
      const aTs = new Date(rawA).getTime();
      const bTs = new Date(rawB).getTime();
      if (Number.isNaN(aTs) && Number.isNaN(bTs)) return 0;
      if (Number.isNaN(aTs)) return 1;
      if (Number.isNaN(bTs)) return -1;
      return (aTs - bTs) * dir;
    }

    if (INVOICE_NUMERIC_FIELDS.has(sortKey)) {
      const aNum = parseFloat(rawA);
      const bNum = parseFloat(rawB);
      const aGood = !Number.isNaN(aNum);
      const bGood = !Number.isNaN(bNum);
      if (!aGood && !bGood) return 0;
      if (!aGood) return 1;
      if (!bGood) return -1;
      return (aNum - bNum) * dir;
    }

    // String fallback (case-insensitive); null/empty sinks to bottom.
    const aHas = rawA != null && rawA !== '';
    const bHas = rawB != null && rawB !== '';
    if (!aHas && !bHas) return 0;
    if (!aHas) return 1;
    if (!bHas) return -1;
    const aStr = String(rawA).toLowerCase();
    const bStr = String(rawB).toLowerCase();
    return aStr.localeCompare(bStr) * dir;
  });
  return out;
}
